import { Inject, Injectable } from '@angular/core';

import { SnLonLat, SnLonLatBounds, SnLonLatLike } from '@lib/geo/helpers';
import { IMarker as _IMarker } from '@lib/geo/map';
import { MapGeometryFacadeService, DistanceUnit } from '@lib/map-geometry';

import { EsriMap } from '../../../map';
import { EsriModules, ESRI_MODULES_TOKEN } from '../../../tokens';
import { IEsriMarker } from '../interfaces';


type IMarker = _IMarker<EsriMap>;

@Injectable()
export class EsriMarkerGeometryService {
  private _esriMarkers = new Map<string, IEsriMarker>();

  constructor(
    @Inject(ESRI_MODULES_TOKEN) private readonly esri: EsriModules,
    private readonly _mapGeometryService: MapGeometryFacadeService<EsriMap>,
  ) {}

  public setMarkersCollection(esriMarkers: Map<string, IEsriMarker>): void {
    this._esriMarkers = esriMarkers;
  }

  public setVisible(esriMarker: IEsriMarker, marker: IMarker, visible: boolean): void {
    if (esriMarker.setVisible && typeof esriMarker.setVisible === 'function') {
      esriMarker.setVisible(visible);
    }
    marker.visible = visible;
  }

  public setPosition(esriMarker: IEsriMarker, marker: IMarker, position: SnLonLat): void {
    if (esriMarker.setDestination && typeof esriMarker.setDestination === 'function') {
      esriMarker.setDestination(position);
    } else {
      esriMarker.setLonLat(position);
    }
    marker.position = position;
  }

  public getMarkerPosition(marker: IMarker): SnLonLat {
    return marker.position;
  }

  public getMarkerCenter(marker: IMarker): { center: number[] } {
    return { center: [marker.position.lon, marker.position.lat] };
  }

  public isVisible(marker: IMarker): boolean {
    return marker.visible;
  }

  public calculateDistance(
    marker1: IMarker,
    marker2: IMarker,
    unit: DistanceUnit = DistanceUnit.MILES,
  ): number {
    const point1 = new this.esri.Point({
      longitude: marker1.position.lon,
      latitude: marker1.position.lat,
      spatialReference: { wkid: 4326 },
    });

    const point2 = new this.esri.Point({
      longitude: marker2.position.lon,
      latitude: marker2.position.lat,
      spatialReference: { wkid: 4326 },
    });

    const polyline = new this.esri.Polyline({
      paths: [
        [
          [point1.longitude, point1.latitude],
          [point2.longitude, point2.latitude],
        ],
      ],
      spatialReference: { wkid: 4326 },
    });

    const distanceKm = this.esri.geodesicLength(polyline, 'miles');
    return this._mapGeometryService.convertDistance(distanceKm, DistanceUnit.MILES, unit);
  }

  public calculateBearing(marker1: IMarker, marker2: IMarker): number {
    const lat1 = (marker1.position.lat * Math.PI) / 180;
    const lat2 = (marker2.position.lat * Math.PI) / 180;
    const deltaLon = ((marker2.position.lon - marker1.position.lon) * Math.PI) / 180;

    const y = Math.sin(deltaLon) * Math.cos(lat2);
    const x =
      Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLon);

    let bearing = Math.atan2(y, x);
    bearing = (bearing * 180) / Math.PI;
    bearing = (bearing + 360) % 360;

    return bearing;
  }

  public isWithinDistance(marker: IMarker, point: SnLonLat, distanceKm: number): boolean {
    const distance = this.calculateDistance(marker, { position: point } as IMarker);
    return distance <= distanceKm;
  }

  public getMarkersBounds(markers: IMarker[]): SnLonLatBounds | null {
    if (markers.length === 0) return null;

    const bounds = new SnLonLatBounds(
      [markers[0].position.lon, markers[0].position.lat],
      [markers[0].position.lon, markers[0].position.lat],
    );

    markers.forEach((marker) => {
      bounds.extend(new SnLonLat(marker.position.lon, marker.position.lat));
    });

    return bounds;
  }

  public getMarkersCenter(markers: IMarker[]): SnLonLat | null {
    if (markers.length === 0) return null;

    const points = markers.map((marker) => ({
      longitude: marker.position.lon,
      latitude: marker.position.lat,
    }));

    const minLon = Math.min(...points.map((p) => p.longitude));
    const maxLon = Math.max(...points.map((p) => p.longitude));
    const minLat = Math.min(...points.map((p) => p.latitude));
    const maxLat = Math.max(...points.map((p) => p.latitude));

    const centerLon = (minLon + maxLon) / 2;
    const centerLat = (minLat + maxLat) / 2;

    return new SnLonLat(centerLon, centerLat);
  }

  public fitBoundsToMarkers(markers: IMarker[]): void {
    if (markers.length === 0) return;

    const bounds = this._mapGeometryService.calculateBoundsFromMarkers(markers);
    if (bounds) {
      this._mapGeometryService.fitBounds(bounds);
    }
  }

  public panToMarkers(markers: IMarker[]): void {
    if (markers.length === 0) return;

    const bounds = this._mapGeometryService.calculateBoundsFromMarkers(markers);
    if (bounds) {
      this._mapGeometryService.fitBounds(bounds);
    }
  }

  public setFollowMode(esriMarker: IEsriMarker, marker: IMarker, followMode: boolean): void {
    if (esriMarker.setFollowMode && typeof esriMarker.setFollowMode === 'function') {
      esriMarker.setFollowMode(followMode);
    }
    if (!marker.metadata) marker.metadata = {};
    marker.metadata.followMode = followMode;
  }

  public getFollowMode(marker: IMarker): boolean {
    return marker.metadata?.followMode || false;
  }

  public setDestination(esriMarker: IEsriMarker, marker: IMarker, dest: SnLonLatLike): void {
    if (esriMarker.setDestination && typeof esriMarker.setDestination === 'function') {
      esriMarker.setDestination(dest);
    }
    if (!marker.metadata) marker.metadata = {};
    marker.metadata.destination = SnLonLat.convert(dest);
  }

  public setDeltas(esriMarker: IEsriMarker, marker: IMarker, seconds: number): void {
    if (esriMarker.setDeltas && typeof esriMarker.setDeltas === 'function') {
      esriMarker.setDeltas(seconds);
    }
    if (!marker.metadata) marker.metadata = {};
    marker.metadata.animationSeconds = seconds;
  }

  public move(esriMarker: IEsriMarker, marker: IMarker): void {
    if (esriMarker.move && typeof esriMarker.move === 'function') {
      esriMarker.move();
    }
    marker.position = esriMarker.getLonLat();
  }

  public animateSetLonLat(
    esriMarker: IEsriMarker,
    marker: IMarker,
    seconds: number,
    moveto: SnLonLatLike,
  ): void {
    if (esriMarker.animateSetLonLat && typeof esriMarker.animateSetLonLat === 'function') {
      esriMarker.animateSetLonLat(seconds, moveto);
    } else {
      esriMarker.setLonLat(moveto);
    }
    setTimeout(() => {
      marker.position = esriMarker.getLonLat();
    }, seconds * 1000);
  }

  public isWithinMapBounds(marker: IMarker): boolean {
    try {
      const bounds = this._mapGeometryService.getBounds();
      const position = marker.position;

      return (
        position.lat >= bounds.getSouth() &&
        position.lat <= bounds.getNorth() &&
        position.lon >= bounds.getWest() &&
        position.lon <= bounds.getEast()
      );
    } catch (error) {
      return true;
    }
  }

  public getAnimationState(marker: IMarker): {
    isAnimating: boolean;
    destination?: SnLonLat;
    seconds?: number;
  } {
    return {
      isAnimating: !!(marker.metadata?.destination && marker.metadata?.animationSeconds),
      destination: marker.metadata?.destination,
      seconds: marker.metadata?.animationSeconds,
    };
  }

  public getMarkerGeometry(marker: IMarker): unknown {
    return {
      type: 'Point',
      coordinates: [marker.position.lon, marker.position.lat],
    };
  }
}
